import type {
  ConfigureEnvironmentRequestV1,
  OperatorDeactivationRequestV1,
  Product,
  PutProductRequestV1,
} from "@strust/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import {
  configureEnvironmentEndpoint,
  deactivateOperatorEndpoint,
  getOperatorEndpoint,
  listOperatorsEndpoint,
  putProductEndpoint,
} from "./endpoints.ts";
import { operatorCredentialsKey } from "./credentials.ts";

export const operatorsKey = ["platform", "operators"] as const;
export const operatorKey = (operatorId: string) => ["platform", "operator", operatorId] as const;

/** Operators of the platform; `enabled` is false without the permission (no request is made). */
export function useOperators(enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: operatorsKey,
    enabled,
    queryFn: ({ signal }) => client.call(listOperatorsEndpoint, { signal }),
  });
}

export function useOperator(operatorId: string, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: operatorKey(operatorId),
    enabled,
    queryFn: ({ signal }) =>
      client.call(getOperatorEndpoint, { params: { id: operatorId }, signal }),
  });
}

/** Both environment mutations answer the updated environment: the operator is refetched. */
function useRefreshOperator(operatorId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: operatorKey(operatorId) }),
      queryClient.invalidateQueries({ queryKey: operatorsKey }),
    ]);
}

export function useConfigureEnvironment(operatorId: string) {
  const client = useApiClient();
  const refresh = useRefreshOperator(operatorId);
  return useMutation({
    mutationFn: (body: ConfigureEnvironmentRequestV1) =>
      client.call(configureEnvironmentEndpoint, {
        params: { id: operatorId },
        body,
      }),
    onSuccess: refresh,
  });
}

export function usePutProduct(operatorId: string) {
  const client = useApiClient();
  const refresh = useRefreshOperator(operatorId);
  return useMutation({
    mutationFn: (input: { product: Product } & PutProductRequestV1) => {
      const { product, ...body } = input;
      return client.call(putProductEndpoint, {
        params: { id: operatorId, product },
        body,
      });
    },
    onSuccess: refresh,
  });
}

/**
 * Deactivates the operator (terminal). The answer is the deactivated operator: it replaces the
 * cached detail at once (no refetch needed), and the list and the credential lists (now revoked) are refetched.
 */
export function useDeactivateOperator(operatorId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OperatorDeactivationRequestV1) =>
      client.call(deactivateOperatorEndpoint, { params: { id: operatorId }, body }),
    onSuccess: (operator) => {
      queryClient.setQueryData(operatorKey(operatorId), operator);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: operatorsKey }),
        queryClient.invalidateQueries({ queryKey: operatorCredentialsKey(operatorId) }),
        // its organization's status and counts change (organizationAccessKey prefix; not imported:
        // organizations.ts imports this file)
        queryClient.invalidateQueries({ queryKey: ["platform", "organization-access"] }),
      ]);
    },
  });
}
